import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import { useNexusBilling } from '../context/NexusBillingContext';
import { FEATURE_META } from '../api/billingApi';

/**
 * Compact allowance indicator for a feature (optionally one AI Tutor tool or
 * one Elite technology). Reads the server summary from NexusBillingContext:
 *
 *   "7 / 10 free this month · 82 credits"        (AI Tutor tool)
 *   "2 / 5 free this month · 10 paid"            (Mock Interviews)
 *   "3 / 10 free pages used"                     (Elite technology, free share)
 *   "Full access until 27 Feb 2027"              (Elite technology, premium)
 *
 * Turns amber when ≤ 2 free units remain so the limit is never a surprise.
 * Renders nothing outside a Nexus session.
 */
export const describeUsage = (features, { feature, subFeature, technology, override }) => {
  if (!features || !features[feature]) return null;
  const f = features[feature];
  if (f.legacyAccess) return { text: 'Full access (plan)', tone: 'ok', legacy: true };
  const buckets = f.monthlyFree ? f.monthlyFree.buckets : (f.freeAllowance ? f.freeAllowance.buckets : []);
  let bucket = null;
  if (subFeature) bucket = buckets.find((b) => b.subFeature === subFeature);
  else if (technology) bucket = buckets.find((b) => b.technology === technology);
  else bucket = buckets[0];
  let freeRemaining = bucket ? bucket.remaining : 0;
  let freeTotal = bucket ? bucket.total : 0;
  let paidRemaining = 0;
  if (f.paid && f.paid.byTechnology) {
    const t = f.paid.byTechnology.find((x) => x.technology === technology);
    paidRemaining = t ? t.remaining : 0;
  } else if (f.paid) paidRemaining = f.paid.remaining || 0;
  let premium = null;
  if (technology && f.premium && Array.isArray(f.premium.byTechnology)) {
    premium = f.premium.byTechnology.find((x) => x.technology === technology) || null;
  }
  if (override) {
    if (override.free) { freeRemaining = override.free.remaining; freeTotal = override.free.total; }
    if (override.paid) paidRemaining = override.paid.remaining;
    if (Object.prototype.hasOwnProperty.call(override, 'premium')) premium = override.premium;
  }
  const meta = FEATURE_META[feature] || { units: 'units' };
  const period = f.quotaPeriod === 'MONTH' ? ' this month' : '';
  if (feature === 'ELITE_MASTERY' && premium && premium.active) {
    const until = premium.expiresAt ? new Date(premium.expiresAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
    const tone = premium.daysRemaining != null && premium.daysRemaining <= 14 ? 'warn' : 'ok';
    return { text: until ? `Full access until ${until}` : 'Full access', tone, freeRemaining, freeTotal, paidRemaining, premium };
  }
  const text = feature === 'ELITE_MASTERY'
    ? `${freeTotal - freeRemaining} / ${freeTotal} free pages used${paidRemaining > 0 ? ` · ${paidRemaining} paid pages left` : ''}`
    : `${freeRemaining} / ${freeTotal} free${period} · ${paidRemaining} paid ${meta.units}`;
  const tone = freeRemaining + paidRemaining === 0 ? 'out' : (freeRemaining <= 2 && paidRemaining === 0 ? 'warn' : 'ok');
  return { text, tone, freeRemaining, freeTotal, paidRemaining, premium };
};

const NexusUsageBadge = ({ feature, subFeature, technology, override, size = 'small', sx = {} }) => {
  const { enabled, features } = useNexusBilling();
  const navigate = useNavigate();
  const info = useMemo(() => describeUsage(features, { feature, subFeature, technology, override }), [features, feature, subFeature, technology, override]);
  if (!enabled || !info) return null;
  const colors = info.tone === 'out' ? { bg: 'rgba(239,68,68,0.08)', fg: '#B91C1C' } : info.tone === 'warn' ? { bg: 'rgba(245,158,11,0.12)', fg: '#B45309' } : { bg: 'rgba(108,59,255,0.08)', fg: '#5020E0' };
  return (
    <Tooltip title={info.legacy ? 'Your existing TSG Nexus plan covers this feature until it expires.'
      : info.premium && info.premium.active ? 'Every page of this technology is open for your access period — including new pages. Click to see your plans.'
        : feature === 'ELITE_MASTERY' ? 'The first pages of every technology are free. Unlock every page of a technology for a fixed period. Click to see plans.'
          : 'Free allowance resets every calendar month. Paid credits never reset. Click to buy more.'}>
      <Box component="span" sx={{ display: 'inline-flex', ...sx }}>
        <Chip
          size={size}
          icon={<BoltRoundedIcon sx={{ fontSize: 16, color: `${colors.fg} !important` }} />}
          label={<Typography component="span" variant="caption" sx={{ fontWeight: 700, color: colors.fg }}>{info.text}</Typography>}
          onClick={() => navigate('/nextgen-builder/plans')}
          sx={{ bgcolor: colors.bg, borderRadius: 2, cursor: 'pointer', '&:hover': { bgcolor: colors.bg, filter: 'brightness(0.97)' } }}
        />
      </Box>
    </Tooltip>
  );
};

export default NexusUsageBadge;
